import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { server } from "../server"; 
import PostsWidgetRedux from "../widgets/PostsWidgetRedux";
import AboutUser from "../components/AboutUser";
import { DocumentTextIcon, UserIcon, UsersIcon, PhotoIcon, CalendarIcon } from '@heroicons/react/24/outline';
import OtherConnectionsWidget from "./Connections/OtherConnectionsWidget";
import EventsParentContainer from "./Events/EventsParentContainer";
import MyImagesProfile from "./Content/MyImagesProfile";

const UserProfileData = ({ initialUser = null }) => {
  const { handle } = useParams();
  const [searchParams] = useSearchParams();
  const [user, setUser] = useState(initialUser);
  const [activeTab, setActiveTab] = useState("Posts");
  const [isLoading, setIsLoading] = useState(!initialUser);
  const { user: currentUser } = useSelector((state) => state.user);
  const highlightedPostId = searchParams.get("postId");

  useEffect(() => {
    if (initialUser?._id && initialUser.handle === handle) {
      setUser(initialUser);
      setIsLoading(false);
      return;
    }

    let isMounted = true;

    const fetchUserByHandle = async () => {
      setIsLoading(true);
      try {
        const response = await axios.get(`${server}/user/user-info/handle/${handle}`);
        if (isMounted) {
          setUser(response.data.user);
        }
      } catch (error) {
        console.error("Error fetching user by handle:", error);
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    fetchUserByHandle();
    return () => {
      isMounted = false;
    };
  }, [handle, initialUser]);

  if (isLoading) {
    return <div>Loading user data...</div>;
  }

  if (!user) {
    return <div>User not found.</div>;
  }
  const userId = user._id;
  const isOwner = userId === currentUser?._id;

  // Define your tabs
  const tabs = [
    { name: "Posts", icon: DocumentTextIcon, current: activeTab === "Posts" },
    { name: "Photos", icon: PhotoIcon, current: activeTab === "Photos" },
    { name: "Connections", icon: UsersIcon, current: activeTab === "Connections" },
    { name: "Events", icon: CalendarIcon, current: activeTab === "Events" },
    { name: "About", icon: UserIcon, current: activeTab === "About" },
  ];

  function classNames(...classes) {
    return classes.filter(Boolean).join(" ");
  }

  const renderTabHeader = (title, copy) => (
    <div className="user-profile-data-tab-header">
      <p className="user-profile-data-tab-eyebrow">User Profile</p>
      <h3>{title}</h3>
      <p>{copy}</p>
    </div>
  );

  return (
    <div className="user-profile-data-content-container">
      {/* Tab navigation */}
      <div className="user-profile-data-tab-navigation">
        <nav className="user-profile-data-tabs" aria-label="Tabs">
          {tabs.map((tab) => (
            <button
              key={tab.name}
              onClick={() => setActiveTab(tab.name)}
              className={classNames(
                tab.current
                  ? "user-profile-data-tab-active"
                  : "user-profile-data-tab-inactive",
                "user-profile-data-tab-button"
              )}
              aria-current={tab.current ? "page" : undefined}
            >
              <tab.icon className="user-profile-data-tab-icon" />
              {tab.name}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === "Posts" && (
        <div className="user-profile-data-tab-content">
          {renderTabHeader("Posts", "Updates, posts, and featured moments from this profile.")}
          <PostsWidgetRedux
            ownerId={userId}
            profileType="User"
            isProfile={true}
            highlightedPostId={highlightedPostId}
          />
        </div>
      )}

      {activeTab === "About" && (
        <div className="user-profile-data-tab-content">
          <AboutUser user={user} />
        </div>
      )}

      {activeTab === "Connections" && (
        <div className="user-profile-data-tab-content">
          {renderTabHeader("Connections", "People and profiles connected to this member.")}
          <OtherConnectionsWidget userId={userId} profileType="User" />
        </div>
      )}

      {activeTab === "Photos" && (
        <div className="user-profile-data-tab-content user-profile-data-tab-content-photos">
          {renderTabHeader("Photos", "Photos and media shared by this profile.")}
          <MyImagesProfile
            userId={userId}
            canAddPhotos={isOwner}
            ownerName={user.name}
            ownerAvatar={user.avatar?.url}
            emptyActionHref={isOwner ? "#profile-post-composer" : undefined}
            emptyMessage={
              isOwner
                ? "Share a photo post to start your gallery."
                : "This profile has not shared any photos yet."
            }
          />
        </div>
      )}

      {activeTab === "Events" && (
        <div className="user-profile-data-tab-content user-profile-data-tab-content-events">
          {renderTabHeader("Events", "Events hosted by or connected to this profile.")}
          <EventsParentContainer ownerId={userId} />
        </div>
      )}
    </div>
  );
};

export default UserProfileData;
